import { observer } from 'mobx-react-lite';
import { Fragment } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui';
import {
  FEED_ROW_SLOT_CLASS,
  FEED_ROW_SLOT_PADDING,
  FeedDensityContext,
  FeedKeyScope,
} from '@/features/action-cards-view/feed';
import {
  ConnectAppsFeedCard,
  useShowsConnectAppsCard,
} from '@/features/feed/components/ConnectAppsFeedCard';
import { FeedGroupHeading } from '@/features/feed/components/FeedGroupHeading';
import type { FeedGroup, FeedGroupLeads } from '@/features/feed/feed-groups';
import type { FeedItem } from '@/features/feed/feed-search';
import { cn } from '@/lib/utils';

import { HOME_FEED_DENSITY, HOME_SECTION_ROWS } from '../need-you-cards';
import { nextOpenCardId } from '../next-open-card';
import { TransitioningRow } from '../row-transition';

import { HomeCard } from './HomeCard';
import { HomeChatPromptCard } from './HomeChatPromptCard';

import type { HomeFeed } from '../use-home-feed';
import type { WaitingForYouState } from '../use-waiting-for-you';

type HomeNeedYouCardsProps = {
  home: WaitingForYouState;
  feed: HomeFeed;
  leads: FeedGroupLeads;
  // Opens the Feed at a section, with all its cards shown
  onShowAll: (sectionId: string) => void;
};

const ROW_SLOT_CLASS = cn(
  FEED_ROW_SLOT_CLASS.home,
  FEED_ROW_SLOT_PADDING[HOME_FEED_DENSITY]
);

export const HomeNeedYouCards = observer(
  ({ home, feed, leads, onShowAll }: HomeNeedYouCardsProps) => {
    const { items, headingsBefore, trailingHeadings } = feed.layout;
    const showsConnectAppsCard = useShowsConnectAppsCard();
    const cardIds = items.flatMap((item) =>
      item.kind === 'provider' ? [item.id] : []
    );
    // After the second row, so the first cards stay on top
    const connectAppsAfterIndex = Math.min(1, items.length - 1);
    const openNextAfter = (cardId: string) => {
      const nextId = nextOpenCardId(cardIds, cardId);
      if (nextId) home.openOnly(nextId);
    };
    const placementAt = (position: number) => ({
      surface: 'home' as const,
      position,
      listSize: items.length,
    });

    const heading = (group: FeedGroup, first: boolean) => {
      const count = group.count + (leads[group.id]?.count ?? 0);
      // Home shows a section's first cards; the rest are a click away on the Feed
      const showsAll = group.level === 0 && count > HOME_SECTION_ROWS;
      return (
        <YStack key={`heading:${group.id}`} className={cn(!first && 'pt-3')}>
          <XStack className="min-w-0 items-center gap-2">
            <span className="min-w-0 flex-1">
              <FeedGroupHeading
                group={{ ...group, count }}
                onToggle={feed.toggleSection}
              />
            </span>
            {showsAll ? (
              <button
                type="button"
                aria-label={`Show all ${count} in ${group.label}`}
                className="focus-ring flex h-7 shrink-0 items-center gap-0.5 rounded-md px-1.5 text-xs text-foreground-muted transition-colors hover:text-foreground"
                onClick={() => onShowAll(group.id)}
              >
                Show all {count}
                <Icons.chevronRight aria-hidden className="size-3.5" />
              </button>
            ) : null}
          </XStack>
          {group.collapsed ? null : leads[group.id]?.rows}
        </YStack>
      );
    };
    const row = (item: FeedItem, index: number) => (
      <TransitioningRow
        transition={
          item.kind === 'provider' ? home.rowTransition(item.id) : undefined
        }
        onEnd={() => home.endRowTransition(item.id)}
        className={ROW_SLOT_CLASS}
      >
        {item.kind === 'chat_prompt' ? (
          <HomeChatPromptCard
            itemId={item.id}
            entry={item.entry}
            home={home}
            placement={placementAt(index)}
          />
        ) : (
          <HomeCard
            surface="web_home"
            placement={placementAt(index)}
            entry={item.entry}
            home={home}
            keyboardTarget={home.isItemExpanded(item.id)}
            leavesWhenDone
            onLeave={() => openNextAfter(item.id)}
          />
        )}
      </TransitioningRow>
    );

    return (
      <YStack role="group" aria-label="Need you">
        <FeedKeyScope>
          <FeedDensityContext.Provider value={HOME_FEED_DENSITY}>
            <YStack data-feed-density={HOME_FEED_DENSITY}>
              {items.map((item, index) => (
                <Fragment key={item.id}>
                  {(headingsBefore[index] ?? []).map((group, groupIndex) =>
                    heading(group, index === 0 && groupIndex === 0)
                  )}
                  {row(item, index)}
                  {showsConnectAppsCard && index === connectAppsAfterIndex ? (
                    <div className={ROW_SLOT_CLASS}>
                      <ConnectAppsFeedCard />
                    </div>
                  ) : null}
                </Fragment>
              ))}
              {trailingHeadings.map((group, groupIndex) =>
                heading(group, items.length === 0 && groupIndex === 0)
              )}
            </YStack>
          </FeedDensityContext.Provider>
        </FeedKeyScope>
      </YStack>
    );
  }
);
